import React from "react";
import { Check, Loader2, AlertTriangle } from "lucide-react";
import { AISparkles } from "@/components/ui/ai-sparkles";
import type { GenerateRationaleResponse, KeywordAdjustment, UnfulfilledLever } from "@/types/ab-tests";
import { UnfulfilledLeversDisclosure } from "./UnfulfilledLeversDisclosure";

interface RationaleSlotProps {
  isGeneratingRationale: boolean;
  rationaleData: GenerateRationaleResponse | null;
  rationaleOpen: boolean;
  keywordAdjustment?: KeywordAdjustment | null;
  unfulfilledLevers?: UnfulfilledLever[] | null;
}

export function RationaleSlot({ isGeneratingRationale, rationaleData, rationaleOpen, keywordAdjustment = null, unfulfilledLevers = null }: RationaleSlotProps) {
  if (!rationaleData && !isGeneratingRationale) return null;
  if (!rationaleOpen) return null;

  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-3 scroll-mt-24">
      <div className="flex items-center gap-2">
        <AISparkles className="h-4 w-4 shrink-0 text-primary" />
        <p className="text-sm font-semibold text-foreground">Signal AI Rationale</p>
      </div>
      {isGeneratingRationale ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Signal AI is composing the rationale…</span>
        </div>
      ) : rationaleData ? (
        <div className="space-y-4">
          <UnfulfilledLeversDisclosure levers={unfulfilledLevers} />
          {keywordAdjustment && (
            <div
              data-testid="keyword-adjustment-disclosure"
              className="flex items-start gap-2.5 rounded-md border border-status-warning-mid bg-status-warning-soft px-3 py-2.5"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-status-warning-strong" strokeWidth={2.5} />
              <p className="text-sm font-medium text-foreground">
                Requested keyword &quot;{keywordAdjustment.requested_keyword}&quot; was adapted, not inserted verbatim
              </p>
            </div>
          )}
          {rationaleData.summary && (
            <p className="text-sm leading-relaxed text-foreground">{rationaleData.summary}</p>
          )}
          {rationaleData.signals && rationaleData.signals.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Signals considered</p>
              <ul className="flex flex-wrap gap-x-5 gap-y-1.5">
                {rationaleData.signals.map((s, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-foreground">
                    <Check className="h-4 w-4 text-status-success-strong shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {rationaleData.phraseChanges && rationaleData.phraseChanges.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Changes</p>
              <div className="rounded-md border border-border overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-px bg-border text-xs">
                  <div className="bg-muted/50 px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Variant A</div>
                  <div className="bg-muted/50 px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Variant B</div>
                  <div className="bg-muted/50 px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Rationale</div>
                  {rationaleData.phraseChanges.map((c, i) => (
                    <React.Fragment key={i}>
                      <div className="bg-card px-3 py-2 text-sm text-foreground">
                        {c.from ? (
                          <span className="rounded px-1.5 py-0.5 bg-status-error-soft line-through decoration-foreground/40">{c.from}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">(added)</span>
                        )}
                      </div>
                      <div className="bg-card px-3 py-2 text-sm text-foreground">
                        {c.to ? (
                          <span className="rounded px-1.5 py-0.5 bg-status-success-soft">{c.to}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">(removed)</span>
                        )}
                      </div>
                      <div className="bg-card px-3 py-2 text-sm text-muted-foreground">{c.reason || "—"}</div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
